import { SegmentedField } from '../../shared/field/segmented_field.js';
import { FieldShell } from '../../shared/field/field_shell.js';
import {
  CountryField,
  type CountryFieldCopy,
  type CountryFieldState,
} from '../../shared/country_field/country_field.js';
import type { CountryCatalogItem } from '../../shared/country_field/country_options.js';
import { DateField } from '../../shared/field/date_field.js';
import { checkoutDateCopy, type CheckoutDateCopy } from './date_copy.js';
import './checkout_fields.css';

export type DocType = 'cpf' | 'passport' | 'dni';

export interface DocumentDraft {
  type: DocType;
  value: string;
  issuingCountry?: string;
  expirationDate?: string;
}

export interface DocumentMaskedInfo {
  cpf?: string;
  passport?: string;
  passportCountry?: string;
  passportValidate?: string;
  dni?: string;
}

export interface DocumentFieldCopy {
  /** Rótulo do seletor de tipo. */
  readonly type: string;
  readonly cpf: string;
  readonly passport: string;
  readonly nationalDoc: string;
  readonly expiry: string;
  readonly issuingCountry: CountryFieldCopy;
  /** Abaixo da validade: o valor que já está em sistema. */
  readonly documentOnFile: (value: string) => string;
  /** CPF em ficha: onde alterar. */
  readonly editInProfileHint: string;
  /** CPF fora de ficha (campo editável), com outro tipo de documento a escolher. */
  readonly cpfNotOnFileHint: string;
  /** CPF fora de ficha (campo editável), sem alternativa no manifesto. */
  readonly cpfNotOnFileNoAlternativeHint: string;
  /** Sob o CPF digitado quando `cpfInvalid`: os dígitos verificadores não batem. */
  readonly cpfInvalid: string;
}

export interface DocumentFieldProps {
  document?: DocumentDraft;
  defaultIssuingCountry?: string;
  maskedInfo?: DocumentMaskedInfo;
  /** Document types the manifest allows (from the field's `allow`). Only these
   *  segments render; a single allowed type drops the toggle entirely. Absent ⇒
   *  all three (defensive default for callers without a manifest). */
  allow?: DocType[];
  /** CPF is on-file from the server profile (a masked CPF exists): the number is
   *  read-only, masked, with "edit in your profile" — the backend resolves it by
   *  traveler id. Without one the CPF is typed here (#5809): it travels on the
   *  wire and fills the gap in the traveler's own profile. Passport/DNI stay
   *  fully editable. */
  cpfOnFile?: boolean;
  /** The typed CPF has 11 digits whose check digits fail (#5935). The caller
   *  decides it — this kit does not validate documents; the field only shows it. */
  cpfInvalid?: boolean;
  /** Prefix for the input/select ids — kept unique per traveler in the
   *  multi-traveler drawer. Absent ⇒ the original `pc-doc-*`/`ctc-doc-*` ids. */
  idPrefix?: string;
  /** O catálogo de países do país emissor (o mesmo do perfil) e o estado dele:
   *  indisponível ⇒ o campo fica só-leitura (fail-closed, sem texto livre). */
  countries: readonly CountryCatalogItem[];
  countriesState: CountryFieldState;
  /** Catálogo indisponível: recarrega (o campo mostra a ação com `retry` na copy). */
  onCountriesRetry?: () => void;
  copy: DocumentFieldCopy;
  /** The expiry calendar's words (`CheckoutFieldCopy.date`). */
  dateCopy: CheckoutDateCopy;
  locale: string;
  onChange: (doc: DocumentDraft) => void;
}

function docNumberLabel(copy: DocumentFieldCopy, type: DocType): string {
  if (type === 'cpf') return copy.cpf;
  if (type === 'passport') return copy.passport;
  return copy.nationalDoc;
}

function DocumentNumberInput({
  currentValue,
  numberLabel,
  numberPlaceholder,
  numberId,
  hint,
  error,
  emit,
}: {
  readonly currentValue: string;
  readonly numberLabel: string;
  readonly numberPlaceholder?: string;
  readonly numberId: string;
  readonly hint?: string;
  readonly error?: string;
  readonly emit: (patch: Partial<DocumentDraft>) => void;
}) {
  const hintId = `${numberId}-hint`;
  const errorId = `${numberId}-error`;
  const describedBy = [error ? errorId : undefined, hint ? hintId : undefined]
    .filter(Boolean)
    .join(' ');
  // Passport/DNI (and a CPF not on file) are editable. The on-file passport/DNI
  // (masked by the backend) is shown as the placeholder so the field stays open
  // for a new value — leaving it blank signals the submit mapper to reuse it.
  return (
    <div style={{ marginTop: 'var(--v2-s-2)' }}>
      <label className="ctc-field-label" htmlFor={numberId}>
        {numberLabel}
      </label>
      <input
        id={numberId}
        type="text"
        className="ctc-input"
        value={currentValue}
        aria-label={numberLabel}
        {...(error ? { 'aria-invalid': true } : {})}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        {...(numberPlaceholder ? { placeholder: numberPlaceholder } : {})}
        onChange={(e) => {
          emit({ value: e.target.value });
        }}
      />
      {error && (
        <p id={errorId} className="ctc-input-error">
          {error}
        </p>
      )}
      {hint && (
        <p id={hintId} className="ctc-input-hint">
          {hint}
        </p>
      )}
    </div>
  );
}

function DocumentNumberReadOnly({
  numberLabel,
  numberId,
  maskedValue,
  hint,
}: {
  readonly numberLabel: string;
  readonly numberId: string;
  readonly maskedValue?: string;
  readonly hint: string;
}) {
  // CPF on file: the number comes from the server profile (masked) and the
  // backend resolves the real one by traveler id; `hint` says where to change it.
  return (
    <div style={{ marginTop: 'var(--v2-s-2)' }}>
      <label className="ctc-field-label" htmlFor={numberId}>
        {numberLabel}
      </label>
      <input
        id={numberId}
        type="text"
        className="ctc-input ctc-input--readonly"
        value={maskedValue ?? ''}
        readOnly
        aria-label={numberLabel}
      />
      <p className="ctc-input-hint">{hint}</p>
    </div>
  );
}

function PassportExtraFields({
  copy,
  countries,
  countriesState,
  onCountriesRetry,
  issuingCountry,
  defaultIssuingCountry,
  expirationDate,
  maskedInfo,
  countryId,
  dateCopy,
  locale,
  emit,
}: {
  readonly copy: DocumentFieldCopy;
  readonly countries: readonly CountryCatalogItem[];
  readonly countriesState: CountryFieldState;
  readonly onCountriesRetry?: () => void;
  readonly issuingCountry: string;
  readonly defaultIssuingCountry?: string;
  readonly expirationDate: string;
  readonly maskedInfo?: DocumentMaskedInfo;
  readonly countryId: string;
  readonly dateCopy: CheckoutDateCopy;
  readonly locale: string;
  readonly emit: (patch: Partial<DocumentDraft>) => void;
}) {
  // O rascunho guarda só o código; o nome vem do catálogo (ou fica vazio, e o
  // campo mostra o código gravado como opção fora do catálogo).
  const code = issuingCountry || defaultIssuingCountry || '';
  const name = countries.find((c) => c.code === code.toUpperCase())?.name ?? '';
  return (
    <div className="ctc-doc-row" style={{ marginTop: 'var(--v2-s-2)' }}>
      <FieldShell label={copy.issuingCountry.label} fieldId={countryId}>
        <CountryField
          id={countryId}
          {...(code ? { value: { code, name } } : {})}
          countries={countries}
          state={countriesState}
          copy={copy.issuingCountry}
          {...(onCountriesRetry ? { onRetry: onCountriesRetry } : {})}
          onChange={(next) => {
            emit({ issuingCountry: next.code });
          }}
        />
      </FieldShell>
      <div className="ctc-field">
        {/* `free`: an expiry is usually ahead, but an expired passport is still typed in. */}
        <DateField
          label={copy.expiry}
          value={expirationDate}
          copy={checkoutDateCopy(dateCopy, copy.expiry, locale)}
          locale={locale}
          timeline="free"
          onChange={(day) => {
            emit({ expirationDate: day });
          }}
        />
        {maskedInfo?.passportValidate && (
          <p className="ctc-input-hint">{copy.documentOnFile(maskedInfo.passportValidate)}</p>
        )}
      </div>
    </div>
  );
}

const ALL_DOC_TYPES: DocType[] = ['cpf', 'passport', 'dni'];

function allowedDocumentTypes(allow?: DocType[]): DocType[] {
  return allow && allow.length > 0 ? allow : ALL_DOC_TYPES;
}

/** O CPF só tem input sem CPF em ficha, então não tem placeholder mascarado. */
function documentPlaceholder(type: DocType, maskedInfo?: DocumentMaskedInfo): string | undefined {
  if (type === 'cpf') return undefined;
  if (type === 'passport') return maskedInfo?.passport;
  return maskedInfo?.dni;
}

/** Dica do CPF editável (sem CPF em ficha). A variante com "ou use
 *  passaporte/documento nacional" só vale quando o toggle de tipos realmente
 *  renderiza — num campo CPF-only ela prometeria uma alternância inexistente. */
function cpfNotOnFileHint(copy: DocumentFieldCopy, hasTypeAlternatives: boolean): string {
  return hasTypeAlternatives ? copy.cpfNotOnFileHint : copy.cpfNotOnFileNoAlternativeHint;
}

function DocumentNumberField({
  type,
  cpfOnFile,
  cpfInvalid,
  hasTypeAlternatives,
  copy,
  numberLabel,
  numberId,
  currentValue,
  maskedInfo,
  emit,
}: {
  readonly type: DocType;
  readonly cpfOnFile?: boolean;
  readonly cpfInvalid?: boolean;
  readonly hasTypeAlternatives: boolean;
  readonly copy: DocumentFieldCopy;
  readonly numberLabel: string;
  readonly numberId: string;
  readonly currentValue: string;
  readonly maskedInfo?: DocumentMaskedInfo;
  readonly emit: (patch: Partial<DocumentDraft>) => void;
}) {
  // CPF em ficha: só-leitura e mascarado — o upstream resolve o CPF pelo id do
  // viajante e o submit não o reenvia. Sem CPF em ficha o campo é editável
  // (#5809): o submit leva o CPF digitado na reserva e o grava no perfil do
  // próprio viajante, só preenchendo a lacuna.
  if (type === 'cpf' && cpfOnFile) {
    return (
      <DocumentNumberReadOnly
        numberLabel={numberLabel}
        numberId={numberId}
        hint={copy.editInProfileHint}
        {...(maskedInfo?.cpf ? { maskedValue: maskedInfo.cpf } : {})}
      />
    );
  }
  return (
    <DocumentNumberInput
      currentValue={currentValue}
      numberLabel={numberLabel}
      numberPlaceholder={documentPlaceholder(type, maskedInfo)}
      numberId={numberId}
      {...(type === 'cpf' ? { hint: cpfNotOnFileHint(copy, hasTypeAlternatives) } : {})}
      {...(type === 'cpf' && cpfInvalid ? { error: copy.cpfInvalid } : {})}
      emit={emit}
    />
  );
}

/** CPF/passport/DNI segmented picker + conditional passport country/expiry.
 *  Renders only the segments in `allow` (a single allowed type drops the
 *  toggle). CPF on file stays read-only (backend-resolved); without one it is typed here. */
export function DocumentField({
  document,
  defaultIssuingCountry,
  maskedInfo,
  allow,
  cpfOnFile,
  cpfInvalid,
  idPrefix = '',
  countries,
  countriesState,
  onCountriesRetry,
  copy,
  dateCopy,
  locale,
  onChange,
}: Readonly<DocumentFieldProps>) {
  const numberId = `${idPrefix}pc-doc-number`;
  const countryId = `${idPrefix}ctc-doc-country`;
  const allowed = allowedDocumentTypes(allow);
  const DOC_OPTIONS = [
    { value: 'cpf' as const, label: copy.cpf },
    { value: 'passport' as const, label: copy.passport },
    { value: 'dni' as const, label: copy.nationalDoc },
  ].filter((opt) => allowed.includes(opt.value));
  const fallbackType: DocType = allowed[0] ?? 'cpf';
  const requestedType: DocType = document?.type ?? fallbackType;
  const currentType: DocType = allowed.includes(requestedType) ? requestedType : fallbackType;
  const currentValue = document?.value ?? '';
  const issuingCountry = document?.issuingCountry ?? '';
  const expirationDate = document?.expirationDate ?? '';
  const numberLabel = docNumberLabel(copy, currentType);

  const emit = (patch: Partial<DocumentDraft>) => {
    onChange({
      type: currentType,
      value: currentValue,
      ...(issuingCountry ? { issuingCountry } : {}),
      ...(expirationDate ? { expirationDate } : {}),
      ...patch,
    });
  };

  const onTypeChange = (type: DocType) => {
    if (type === currentType) return;
    if (type === 'passport') {
      onChange({
        type,
        value: '',
        ...(defaultIssuingCountry ? { issuingCountry: defaultIssuingCountry } : {}),
      });
    } else {
      onChange({ type, value: '' });
    }
  };

  return (
    <div className="ctc-field">
      {allowed.length > 1 && (
        <SegmentedField
          label={copy.type}
          options={DOC_OPTIONS}
          value={currentType}
          onChange={onTypeChange}
        />
      )}
      <DocumentNumberField
        type={currentType}
        cpfOnFile={cpfOnFile}
        cpfInvalid={cpfInvalid}
        hasTypeAlternatives={allowed.length > 1}
        copy={copy}
        numberLabel={numberLabel}
        numberId={numberId}
        currentValue={currentValue}
        maskedInfo={maskedInfo}
        emit={emit}
      />
      {currentType === 'passport' && (
        <PassportExtraFields
          copy={copy}
          countries={countries}
          countriesState={countriesState}
          {...(onCountriesRetry ? { onCountriesRetry } : {})}
          issuingCountry={issuingCountry}
          defaultIssuingCountry={defaultIssuingCountry}
          expirationDate={expirationDate}
          maskedInfo={maskedInfo}
          countryId={countryId}
          dateCopy={dateCopy}
          locale={locale}
          emit={emit}
        />
      )}
    </div>
  );
}
